import { useState } from "react";
import { Layers } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Tabitha Studio — Contact Page
// Submits to /.netlify/functions/contact → Resend
// ─────────────────────────────────────────────────────────────────────────────

const C        = "#2D2926";
const PINK     = "#C9A0A0";
const OFFWHITE = "#F7F4F1";
const BORDER   = "#E0D9D3";
const MUTED    = "#9e8f88";
const NAV_BG   = "#FFFFFF";

// ─────────────────────────────────────────────────────────────────────────────
// Inline auth button — full email flow, mirrors AuthButton in App
// ─────────────────────────────────────────────────────────────────────────────
function NavAuthButton({ user, onSignIn, onSignOut }) {
  const [showInput, setShowInput] = useState(false);
  const [email,     setEmail]     = useState("");
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState(null);
  const [menuOpen,  setMenuOpen]  = useState(false);

  function handleSend() {
    if (!email.trim()) return;
    setSending(true); setError(null);
    onSignIn(email.trim())
      .then(function() { setSent(true); setSending(false); })
      .catch(function(err) { setError(err.message || "Something went wrong."); setSending(false); });
  }

  if (user) {
    const initials = (user.email || "U").slice(0, 2).toUpperCase();
    return (
      <div style={{ position: "relative" }}>
        <button onClick={function() { setMenuOpen(function(o) { return !o; }); }}
          style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid #C9A0A0", overflow: "hidden", cursor: "pointer", backgroundColor: "#f5f4f2", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C, fontFamily: "system-ui,sans-serif" }}>{initials}</span>
        </button>
        {menuOpen && (
          <>
            <div onClick={function() { setMenuOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
            <div style={{ position: "absolute", right: 0, top: 40, zIndex: 100, backgroundColor: "#fff", borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.15)", border: "1px solid #e8e4e0", minWidth: 180, overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0eeec" }}>
                <div style={{ fontSize: 10, color: "#a8a29e", fontFamily: "system-ui,sans-serif" }}>{user.email}</div>
              </div>
              <button onClick={function() { setMenuOpen(false); if (onSignOut) onSignOut(); }}
                style={{ display: "block", width: "100%", padding: "12px 16px", textAlign: "left", background: "none", border: "none", fontSize: 11, color: "#c0604a", fontWeight: 600, cursor: "pointer", fontFamily: "system-ui,sans-serif" }}>
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  if (sent) {
    return <div style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, color: "#7aad7a", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "system-ui,sans-serif" }}>Check your email ✓</div>;
  }

  if (showInput) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
        <input autoFocus type="email" placeholder="your@email.com" value={email}
          onChange={function(e) { setEmail(e.target.value); setError(null); }}
          onKeyDown={function(e) { if (e.key === "Enter") handleSend(); if (e.key === "Escape") { setShowInput(false); setEmail(""); } }}
          style={{ padding: "7px 12px", borderRadius: 999, border: error ? "1px solid #c0604a" : "1px solid #D8D0C8", fontSize: 11, fontFamily: "system-ui,sans-serif", outline: "none", width: 180, color: C }} />
        <button onClick={handleSend} disabled={sending || !email.trim()}
          style={{ padding: "7px 14px", borderRadius: 999, border: "none", backgroundColor: "#C9A0A0", color: C, fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", cursor: sending ? "default" : "pointer", fontFamily: "system-ui,sans-serif", opacity: sending ? 0.6 : 1 }}>
          {sending ? "Sending…" : "Send link"}
        </button>
      </div>
    );
  }

  return (
    <button onClick={function() { setShowInput(true); }}
      style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#6b5e58", cursor: "pointer", fontFamily: "system-ui,sans-serif" }}>
      Sign In
    </button>
  );
}

const TOPICS = [
  "General question",
  "Feedback",
  "Partnership or enterprise",
  "Press or media",
  "Something else",
];

const inputStyle = {
  width: "100%",
  fontFamily: "system-ui,sans-serif",
  fontSize: 14,
  color: C,
  background: OFFWHITE,
  border: "0.5px solid " + BORDER,
  borderRadius: 8,
  padding: "11px 14px",
  outline: "none",
  boxSizing: "border-box",
  WebkitAppearance: "none",
  appearance: "none",
};

function Label({ children }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: "0.2em",
      textTransform: "uppercase", color: MUTED,
      marginBottom: 7, fontFamily: "system-ui,sans-serif",
    }}>
      {children}
    </div>
  );
}

// Inline sign-in widget — mirrors the AuthButton email flow
function SignInWidget({ onSignIn }) {
  const [showInput, setShowInput] = useState(false);
  const [email,     setEmail]     = useState("");
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState(null);

  function handleSend() {
    if (!email.trim()) return;
    setSending(true);
    setError(null);
    onSignIn(email.trim()).then(function() {
      setSent(true);
      setSending(false);
    }).catch(function(err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSending(false);
    });
  }

  if (sent) {
    return (
      <div style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7aad7a", fontFamily: "system-ui,sans-serif" }}>
        Check your email ✓
      </div>
    );
  }

  if (showInput) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
        <input
          autoFocus
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={function(e) { setEmail(e.target.value); setError(null); }}
          onKeyDown={function(e) {
            if (e.key === "Enter") handleSend();
            if (e.key === "Escape") { setShowInput(false); setEmail(""); setError(null); }
          }}
          style={{ padding: "7px 12px", borderRadius: 999, border: error ? "1px solid #c0604a" : "1px solid #D8D0C8", fontSize: 11, fontFamily: "system-ui,sans-serif", outline: "none", width: 180, color: C }}
        />
        <button
          onClick={handleSend}
          disabled={sending || !email.trim()}
          style={{ padding: "7px 14px", borderRadius: 999, border: "none", backgroundColor: PINK, color: C, fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", cursor: sending ? "default" : "pointer", fontFamily: "system-ui,sans-serif", opacity: sending ? 0.6 : 1 }}
        >
          {sending ? "Sending…" : "Send link"}
        </button>
        {error && (
          <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, fontSize: 10, color: "#c0604a", fontFamily: "system-ui,sans-serif", whiteSpace: "nowrap" }}>{error}</div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={function() { setShowInput(true); }}
      style={{ padding: "7px 12px", borderRadius: 999, border: "1px solid #D8D0C8", backgroundColor: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#6b5e58", cursor: "pointer", fontFamily: "system-ui,sans-serif" }}
    >
      Sign In
    </button>
  );
}

export default function ContactPage({ onNavigate, onNewDesign, user, onSignIn, onSignOut }) {
  const [fields,   setFields]   = useState({ name: "", email: "", topic: "", message: "" });
  const [status,   setStatus]   = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [focused,  setFocused]  = useState(null);
  const sending = status === "sending";

  function handleChange(e) {
    const { name, value } = e.target;
    setFields(function(prev) { return Object.assign({}, prev, { [name]: value }); });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch("/.netlify/functions/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        const data = await res.json().catch(function() { return {}; });
        setErrorMsg(data.error || "Something went wrong — please try again.");
        setStatus("error");
      }
    } catch (_err) {
      setErrorMsg("Could not send — please check your connection and try again.");
      setStatus("error");
    }
  }

  function inputProps(name) {
    return {
      name,
      value: fields[name],
      onChange: handleChange,
      onFocus: function() { setFocused(name); },
      onBlur: function() { setFocused(null); },
      style: Object.assign({}, inputStyle, focused === name
        ? { borderColor: C, background: NAV_BG }
        : {}),
    };
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: OFFWHITE, fontFamily: "system-ui,sans-serif" }}>

      {/* ── Studio-style sticky dusty rose header ── */}
      <div style={{ margin:0, padding:"18px 28px", backgroundColor:PINK, borderBottom:"1px solid #b88f8f", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:60 }}>
        <button onClick={function() { onNavigate("step1"); }} style={{ display:"flex", alignItems:"center", gap:12, background:"none", border:"none", padding:0, cursor:"pointer" }}>
          <Layers style={{ color:C, width:24, height:24, flexShrink:0 }} />
          <div style={{ textAlign:"left" }}>
            <div style={{ margin:0, padding:0, fontSize:21, fontWeight:700, letterSpacing:"-0.02em", color:C, fontFamily:"system-ui,sans-serif" }}>Tabitha</div>
            <div style={{ margin:0, padding:0, fontSize:10, fontWeight:700, letterSpacing:"0.24em", color:"#6f555f", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>Contact Us</div>
          </div>
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <button onClick={function() { onNavigate("gallery"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            Gallery
          </button>
          <button onClick={function() { onNavigate("step1"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            New Design
          </button>
          <NavAuthButton user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
          <button onClick={function() { onNavigate("collection"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            My Collection
          </button>
          <button style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"default", fontFamily:"system-ui,sans-serif", opacity:0.45 }}>
            Contact Us
          </button>
        </div>
      </div>

      {/* ── Page body ── */}
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "48px 24px 96px" }}>

        {/* ── Page heading ── */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h1 style={{ margin: "0 0 14px", fontSize: 36, fontWeight: 700, color: C, letterSpacing: "-0.03em", lineHeight: 1.15, fontFamily: "system-ui,sans-serif" }}>
            Get in Touch
          </h1>
          <p style={{ margin: 0, fontSize: 15, color: MUTED, lineHeight: 1.7, fontFamily: "system-ui,sans-serif", maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
            Questions, feedback, or just want to talk about our design and production tool — we're here.
          </p>
        </div>

        {/* ── Card ── */}
        <div style={{
          backgroundColor: NAV_BG,
          border: "0.5px solid " + BORDER,
          borderRadius: 16,
          padding: "32px 28px",
        }}>
          {status === "success" ? (
            <div style={{ textAlign: "center", padding: "32px 16px" }}>
              <div style={{
                width: 52, height: 52, borderRadius: "50%",
                backgroundColor: C,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px",
              }}>
                <span style={{ fontSize: 22, color: PINK }}>✓</span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C, letterSpacing: "-0.02em", marginBottom: 8, fontFamily: "system-ui,sans-serif" }}>
                Thank you — message received.
              </div>
              <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.65, fontFamily: "system-ui,sans-serif" }}>
                We'll be in touch within a few business days.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div>
                  <Label>Your name</Label>
                  <input type="text" placeholder="First and last name" required {...inputProps("name")} />
                </div>
                <div>
                  <Label>Email address</Label>
                  <input type="email" placeholder="you@example.com" required {...inputProps("email")} />
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <Label>Topic</Label>
                <select
                  name="topic" required value={fields.topic} onChange={handleChange}
                  onFocus={function() { setFocused("topic"); }}
                  onBlur={function() { setFocused(null); }}
                  style={Object.assign({}, inputStyle, {
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239e8f88' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 14px center",
                    paddingRight: 36, cursor: "pointer",
                    color: fields.topic ? C : MUTED,
                    borderColor: focused === "topic" ? C : BORDER,
                    background: focused === "topic" ? NAV_BG : OFFWHITE,
                  })}
                >
                  <option value="" disabled>Select a topic</option>
                  {TOPICS.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
                </select>
              </div>

              <div style={{ marginBottom: 24 }}>
                <Label>Message</Label>
                <textarea
                  placeholder="What's on your mind?" required rows={5}
                  {...inputProps("message")}
                  style={Object.assign({}, inputStyle, {
                    resize: "vertical", minHeight: 120, lineHeight: 1.65,
                    borderColor: focused === "message" ? C : BORDER,
                    background: focused === "message" ? NAV_BG : OFFWHITE,
                  })}
                />
              </div>

              {status === "error" && errorMsg && (
                <div style={{ fontSize: 12, color: "#c0604a", marginBottom: 16, fontFamily: "system-ui,sans-serif" }}>
                  {errorMsg}
                </div>
              )}

              <button
                type="submit" disabled={sending}
                style={{
                  width: "100%", backgroundColor: sending ? "#7a6e6a" : C,
                  color: OFFWHITE, fontFamily: "system-ui,sans-serif",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase",
                  border: "none", borderRadius: 999, padding: "14px 28px",
                  cursor: sending ? "not-allowed" : "pointer",
                  transition: "background 0.15s", opacity: sending ? 0.7 : 1,
                }}
              >
                {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ textAlign: "center", padding: "24px 0 32px", fontSize: 9, color: "#b8b0a8", letterSpacing: "0.15em", fontFamily: "system-ui,sans-serif" }}>
        © Tabitha Studio 2026 ·{" "}
        <a href="/privacy.html" style={{ color: "#b8b0a8", textDecoration: "underline" }}>Privacy Policy</a>
        {" · "}
        <a href="/terms.html" style={{ color: "#b8b0a8", textDecoration: "underline" }}>Terms &amp; Conditions</a>
        {" · "}
        <a href="/faq.html" style={{ color: "#b8b0a8", textDecoration: "underline" }}>FAQ</a>
      </div>

    </div>
  );
}
